
import React, { useContext } from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import BugReportIcon from '@mui/icons-material/BugReport';
import { Globe, SquareCheck } from 'lucide-react';
import { useFlags } from '@flagsmith/flagsmith/react';

import { Auth, useLoggedInUser } from 'src/services/api';
import { LANDLORD_HOME_PATH } from 'src/routes';
import { Flex, FlexColumn } from 'src/components/flex';
import Link from 'src/components/link/Link';
import NavigationLink, { FeatureFlagNavLink } from 'src/components/layout/landlord/NavigationLink';
import { LogOutIcon } from 'src/icons';
import { links } from 'src/components/layout/landlord/links';
import { ProfileImageRounded } from 'src/components/ProfileImageRounded';
import { DesktopNavigationInboxesLink } from 'src/components/layout/landlord/DesktopNavigationInboxesLink';
import { DesktopNavigationPropertiesLink } from 'src/components/layout/landlord/DesktopNavigationPropertiesLink';
import { EFeatureFlag } from 'src/utils/feature-flags';
import { AuthenticatedStateContext } from 'src/authenticated-state/context';
import { getTimezoneAbbr } from 'src/utils/format-date-time';
import { SYSTEM_TIMEZONE_PREFIX, SIDEBAR_SYSTEM_TIMEZONE_PREFIX } from 'src/components/layout/landlord/DesktopNavigationSidebar';

type Props = {
    closeNavigationDrawer: () => void;
}

export default function MobileNavigationDrawer({
    closeNavigationDrawer
}: Props) {
    const handleLogout = () => {
        closeNavigationDrawer();
        Auth.logout();
    };
    const navigate = useNavigate();
    const flags = useFlags([EFeatureFlag.FEATURE_FLAG_ROUTE, EFeatureFlag.TRIAGE_INBOX_PAGE]);

    const { data: loggedInUser } = useLoggedInUser();
    const authenticatedState = useContext(AuthenticatedStateContext);
    const timezoneOverride = authenticatedState?.userSettings?.timezone_override ?? null;
    const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneLabel = timezoneOverride ?
        `${timezoneOverride} (${getTimezoneAbbr(timezoneOverride)})` :
        `System timezone (${systemTimezone})`;
    const mobileTimezoneLabel = timezoneLabel.replace(SYSTEM_TIMEZONE_PREFIX, SIDEBAR_SYSTEM_TIMEZONE_PREFIX).trim();

    const handleClickReportABug = async () => {
        const feedback = Sentry.getFeedback();
        const form = await feedback?.createForm();
        form?.appendToDom();
        form?.open();
    };

    const handleGoToSettings = () => {
        navigate('/landlord/inner/settings');
        closeNavigationDrawer();
    };

    return (
        <Drawer
            open
            variant="temporary"
            onClose={closeNavigationDrawer}
            sx={{ '& .MuiDrawer-paper': { width: '75%', maxWidth: 300 }, }}
        >
            <Box height="100vh">
                <FlexColumn height="100%" justifyContent="space-between" alignItems="stretch">
                    <FlexColumn alignItems="stretch">
                        <Box justifyContent="center" alignItems="center">
                            <Link href={LANDLORD_HOME_PATH} onClick={closeNavigationDrawer}>
                                <Flex height={60} p={1} pl={2} alignItems="center">
                                    <img
                                        src="/img/reffie-logos/brain-transparent-256.png"
                                        alt="reffie-logo"
                                        height="40px"
                                    />
                                    <Typography color="primary">
                                        Reffie
                                    </Typography>
                                </Flex>
                            </Link>
                        </Box>

                        <List>
                            {flags.feature_flag_route?.enabled && <FeatureFlagNavLink onNavigationChange={closeNavigationDrawer} />}
                            {flags.triage_inbox_page?.enabled && (
                                <NavigationLink
                                    text="Bulk Triage"
                                    icon={<SquareCheck />}
                                    link="/landlord/inner/bulk-triage"
                                    onClick={closeNavigationDrawer}
                                />
                            )}
                            <DesktopNavigationInboxesLink onNavigationChange={closeNavigationDrawer} isSidebarCollapsed={false} />
                            {links.map((link) => (
                                link.text === 'Properties' ? (
                                    <DesktopNavigationPropertiesLink
                                        key="properties"
                                        onNavigationChange={closeNavigationDrawer}
                                    />
                                ) : (
                                    <NavigationLink
                                        key={link.text}
                                        text={link.text}
                                        icon={link.icon}
                                        link={link.link}
                                        onClick={closeNavigationDrawer}
                                    />
                                )
                            ))}
                        </List>
                    </FlexColumn>

                    <List>
                        {loggedInUser && (
                            <Flex px={1} py={0} alignItems="center" gap={1} flexDirection={'row'} sx={{
                                cursor: 'pointer',
                                userSelect: 'none'
                            }} onClick={handleGoToSettings}
                            >
                                <ProfileImageRounded url={loggedInUser.profile_photo_url} userName={loggedInUser.name} width={32} height={32} />
                                {(
                                    <FlexColumn alignItems="start" px={1} py={2} gap={1} textAlign="start"
                                        justifyContent="start"
                                    >
                                        <Typography fontWeight="600" fontSize={14} lineHeight="120%"
                                            color="rgba(66, 64, 58, 1)"
                                        >{loggedInUser.name}</Typography>
                                        <Typography variant="body2" fontWeight={400}
                                            color="rgba(158, 157, 152, 1)"
                                        >{loggedInUser.email}</Typography>
                                        <Tooltip title={timezoneLabel}>
                                            <Flex alignItems="center" gap={0.5} minWidth={0}>
                                                <Globe size={11} color="rgba(158, 157, 152, 1)" style={{ flexShrink: 0 }} />
                                                <Typography variant="body2" fontSize={11}
                                                    color="rgba(158, 157, 152, 1)" className="ellipsisText"
                                                >{mobileTimezoneLabel}</Typography>
                                            </Flex>
                                        </Tooltip>
                                    </FlexColumn>)}
                            </Flex>)}
                        <NavigationLink
                            key="report-a-bug-desktop"
                            text={'Report a Bug'}
                            icon={<BugReportIcon />}
                            link=""
                            onClick={handleClickReportABug}
                        />
                        <NavigationLink
                            text="Logout"
                            icon={<LogOutIcon />}
                            onClick={handleLogout}
                        />
                    </List>
                </FlexColumn>
            </Box>
        </Drawer>

    );
}
